//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import twitterText from 'twitter-text';

import {
  ISocialTextEntity,
  SocialTextEntityType,
  SocialTextParseMethod,
} from '@utils/social-text-utils/socialTextUtilsModel';
import { getHashtagWithoutHashSymbol } from '@utils/social-text-utils/socialTextUtils';

/**
 * Takes text as input and parses it to return an array of typed Twitter text entities.
 * For example, the input 'Hello @curalate! #shippy test https://abc.com' would result in output:
 * [
 *   { type: 'text', value: 'Hello '},
 *   { type: 'mention', value: '@curalate'},
 *   { type: 'text', value: '! '},
 *   { type: 'hashtag', value: '#shippy'},
 *   { type: 'text', value: ' test '},
 *   { type: 'url', value: 'https://abc.com'},
 * ]
 *
 * @returns ISocialTextParsedEntity[] The parsed array of objects with properties
 * { type, value }, where type is one of SocialTextParsedEntityType.
 */
export const parseTwitterText: SocialTextParseMethod = (text) => {
  const extractedTwitterEntities = twitterText.extractEntitiesWithIndices(text);
  if (extractedTwitterEntities.length === 0) {
    return [
      {
        type: SocialTextEntityType.Text,
        value: text,
        start: 0,
        end: text.length,
      },
    ];
  }

  const parsedTextValues: ISocialTextEntity[] = [];
  let currIndex = 0;
  extractedTwitterEntities.map((entity, index) => {
    const entityStartIndex = entity.indices[0];
    const entityEndIndex = entity.indices[1];

    if (entityStartIndex > currIndex) {
      const precedingText = text.slice(currIndex, entityStartIndex);
      parsedTextValues.push({
        type: SocialTextEntityType.Text,
        value: precedingText,
        start: currIndex,
        end: entityStartIndex,
      });
    }

    const parsedEntityValue = (() => {
      if ('hashtag' in entity) {
        return {
          type: SocialTextEntityType.Hashtag,
          value: `#${entity.hashtag}`,
          start: entityStartIndex,
          end: entityEndIndex,
        };
      } else if ('url' in entity) {
        return {
          type: SocialTextEntityType.Url,
          value: entity.url,
          start: entityStartIndex,
          end: entityEndIndex,
        };
      } else if ('screenName' in entity) {
        return {
          type: SocialTextEntityType.Mention,
          value: `@${entity.screenName}`,
          start: entityStartIndex,
          end: entityEndIndex,
        };
      } else {
        return {
          type: SocialTextEntityType.Cashtag,
          value: `$${entity.cashtag}`,
          start: entityStartIndex,
          end: entityEndIndex,
        };
      }
    })();

    parsedTextValues.push(parsedEntityValue);

    if (index === extractedTwitterEntities.length - 1) {
      if (text.length > entityEndIndex) {
        const remainingText = text.slice(entityEndIndex);
        parsedTextValues.push({
          type: SocialTextEntityType.Text,
          value: remainingText,
          start: entityEndIndex,
          end: text.length,
        });
      }
    }

    currIndex = entityEndIndex;
  });

  return parsedTextValues;
};

function extractTwitterNameFromMention(mention: string): string {
  return mention.replace('@', '');
}

/**
 * Takes a twitter mention and returns the Twitter link corresponding to the page tag
 * @example createTwitterMentionLink('@username') = https://twitter.com/username
 *
 * @returns string The mention link
 */
export function createTwitterMentionLink(userName: string): string {
  return `https://twitter.com/${extractTwitterNameFromMention(userName)}`;
}

/**
 * Takes a hashtag and returns the Twitter link corresponding to the hashtag
 * @example createTwitterHashtagLink('#summer') = https://twitter.com/hashtag/summer
 *
 * @returns string The hashtag link
 */
export function createTwitterHashtagLink(hashtag: string): string {
  return `https://twitter.com/hashtag/${getHashtagWithoutHashSymbol(hashtag)}`;
}

/**
 * Takes a cashtag and returns the Twitter link corresponding to the cashtag
 * @example createTwitterCashtagLink('$someCashtag') = https://twitter.com/search?q%24someCashtag
 *
 * @returns string The cashtag search link
 */
export function createTwitterCashtagLink(cashtag: string): string {
  return `https://twitter.com/search?q=${cashtag.replace('$', '%24')}`;
}
